import { flash, Label, Modal, roundNumber, SubmitButton } from "@xola/ui-kit";
import {
    FormField,
    handleSuccessfulSubmission,
    InputField,
    isExperiencePurchaseItem,
    isMembershipItem,
    isMistPurchase,
    requiredNumberMoreThanZero,
    requiredString,
} from "common";
import { Field, FieldProps } from "formik";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { LineItemObject, LineItemType, Purchase, PurchaseItem } from "types";
import { boolean, object } from "yup";
import { api } from "../../../api/api";
import { isPackageItem } from "../../../models/Purchase";
import { updateCache } from "../../../utils/updateCache";
import { Currency } from "../../Common/Currency";
import { NotifyCustomerCheckbox } from "../../Common/NotifyCustomerCheckbox";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { CurrencyInputField } from "../../Field/CurrencyInputField";
import { PaymentBreakdown } from "../PaymentBreakdown";
import { PurchaseItemSelect } from "../PurchaseItemSelect";
import { PurchaseItemSummaryTile } from "../PurchaseItemSummaryTile";

const validationSchema = object().shape({
    amount: requiredNumberMoreThanZero(),
    comment: requiredString(),
    notify: boolean(),
});

interface PurchaseReduceBookingValueModalProps {
    isOpen: boolean;
    purchase: Purchase;
    item: PurchaseItem;
    onClose: () => void;
    returnAll?: boolean;
    isCancelAction?: boolean;
}

export const PurchaseReduceBookingValueModal = ({
    isOpen,
    purchase,
    item,
    onClose,
    returnAll = false,
    isCancelAction = false,
}: PurchaseReduceBookingValueModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent
                purchase={purchase}
                item={item}
                returnAll={returnAll}
                isCancelAction={isCancelAction}
                onClose={onClose}
            />
        </Modal>
    );
};

interface ModalContentProps {
    purchase: Purchase;
    item: PurchaseItem;
    onClose: () => void;
    returnAll: boolean;
    isCancelAction: boolean;
}

const ModalContent = ({ purchase, item, onClose, returnAll, isCancelAction }: ModalContentProps) => {
    const [selectedItem, setSelectedItem] = useState<PurchaseItem>(item);

    // Purchase item data can be updated in the background, so we need to use the most recent data for the payload and summary
    const selectedPurchaseItem = purchase.items.find((item) => item.id === selectedItem.id);
    // Exclude items that no longer have any value — there's nothing to reduce, so they shouldn't appear in
    // the selector or the cancel-all "Next" sequence; the excess is returned afterwards via "Refund Excess
    // Payment".
    const allowedItemsToReduceValue = isCancelAction
        ? purchase.items.filter((item) => !isMembershipItem(item) && item.amount !== 0)
        : purchase.items;

    const {
        mutateAsync: reduceBookingValue,
        isLoading,
        isSuccess,
    } = useMutation(async (payload: ReturnType<typeof getPayload>) => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
        return data;
    });

    useEffect(() => {
        /** For cancellation action, we don't want to show membership items in product selector */
        if (!isCancelAction || !selectedItem || !isMistPurchase(purchase)) {
            return;
        }

        if (
            allowedItemsToReduceValue.length > 0 &&
            !allowedItemsToReduceValue.some((item) => item.id === selectedItem.id)
        ) {
            setSelectedItem(allowedItemsToReduceValue[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCancelAction, allowedItemsToReduceValue]);

    if (!selectedPurchaseItem) {
        return null;
    }

    const shouldShowPurchaseItemSelector =
        (isMistPurchase(purchase) && allowedItemsToReduceValue.length > 1) || isPackageItem(selectedItem);

    const handleSubmit = async (values: any) => {
        try {
            const updatedPurchase = await reduceBookingValue(getPayload(purchase, selectedPurchaseItem, values));
            const nextItem = updatedPurchase.items.find((item) => item.id !== selectedItem.id && item.amount !== 0)!;

            if (returnAll && nextItem) {
                setSelectedItem(nextItem);
                updateCache("purchases", updatedPurchase);
            } else {
                handleSuccessfulSubmission(() => {
                    updateCache("purchases", updatedPurchase);
                    onClose();
                });
            }
        } catch {
            flash.danger("Error reducing booking value");
        }
    };

    return (
        <XolaFormik
            enableReinitialize
            validateOnChange
            validateOnBlur={false}
            validationSchema={validationSchema}
            initialValues={{
                amount: roundNumber(purchase.currency, selectedPurchaseItem.amount),
                comment: returnAll || isCancelAction ? "Cancellation" : "",
                notify: true,
            }}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }) => (
                <>
                    <Modal.Header>Reduce Booking Value</Modal.Header>

                    <Modal.Body className="flex flex-wrap">
                        {shouldShowPurchaseItemSelector && (
                            <>
                                <Label>Select Reservation</Label>
                                <div className="mb-4 w-full">
                                    <PurchaseItemSelect
                                        items={allowedItemsToReduceValue}
                                        value={selectedItem}
                                        className="flex-1"
                                        onChange={(item: PurchaseItem) => {
                                            setSelectedItem(item);
                                        }}
                                    />
                                </div>
                            </>
                        )}

                        <div className="flex w-full flex-wrap space-x-4">
                            <fieldset className="flex-1 space-y-4" disabled={isLoading}>
                                <PurchaseItemSummaryTile purchase={purchase} item={selectedPurchaseItem} />

                                <FormField name="amount" label="Amount">
                                    <div className="flex flex-col items-center space-x-2">
                                        <CurrencyInputField isRequired placeholder="Amount" />
                                        <div className="mt-1 w-full space-x-1 px-1 text-right text-sm text-gray-dark">
                                            <span>up to</span>
                                            <Currency shouldRemoveTrailingZeroes={false}>
                                                {selectedPurchaseItem.amount}
                                            </Currency>
                                        </div>
                                    </div>
                                </FormField>

                                <FormField name="comment" label="Comment">
                                    <InputField isRequired placeholder="Reduce booking value" />
                                </FormField>
                            </fieldset>

                            <div className="flex-1">
                                <div className="rounded-lg bg-gray-lighter p-8">
                                    <Field>
                                        {({ form }: FieldProps) => (
                                            <PaymentBreakdown
                                                isEnabled={!isSuccess}
                                                payload={getPayload(purchase, selectedPurchaseItem, form.values)}
                                                purchase={purchase}
                                                item={selectedPurchaseItem}
                                            />
                                        )}
                                    </Field>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <fieldset className="flex justify-end space-x-4" disabled={isLoading}>
                            <Field name="notify">
                                {({ field, form }: FieldProps) => (
                                    <NotifyCustomerCheckbox
                                        isChecked={field.value}
                                        onChange={async (e) => {
                                            form.setFieldValue("notify", e.target.checked);
                                        }}
                                    />
                                )}
                            </Field>

                            <SecondaryButton onClick={onClose}>Skip</SecondaryButton>

                            <SubmitButton isLoading={isLoading} isSuccess={isSuccess} onClick={handleSubmit}>
                                Save Changes
                            </SubmitButton>
                        </fieldset>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const getPayload = (purchase: Purchase, item: PurchaseItem, values: any) => {
    const retainPriceStrategy = isMembershipItem(item) || isExperiencePurchaseItem(item);
    return {
        ops: [
            {
                path: `/items/${item.id}/lineItems`,
                op: "addLineItem",
                data: {
                    amount: -1 * roundNumber(purchase.currency, values.amount ?? 0),
                    name: values.comment || "Reduce booking value", // Send a default name if empty
                    object: LineItemObject.DEFAULT,
                    type: LineItemType.ADJUSTMENT,
                    _emailOverrides: { sendEmailToTraveler: values.notify },
                    ...(retainPriceStrategy ? { _priceStrategy: "retain" } : null),
                },
            },
        ],
        _emailOverrides: { sendEmailToTraveler: values.notify },
    };
};
